import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { GoBackButtonWithTitle, NewConfirmDialog } from 'components';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import {
  RegisteredEquipmentsContainer,
  RequestedEquipmentsContainer,
} from 'features/equipments/components';
import { CategoryFilter } from 'features/equipments/components/CategoryFilter';
import { useEquipmentFilter } from 'features/equipments/hooks';
import { useSessionState } from 'hooks';
import { Links } from 'settings';
import { theme } from 'styles';
import { Language } from 'types';

import { useDeleteEquipment } from './hooks';
import { PageW, ContentW, HeaderActionsW, PageTitle, Header } from './styled';

export function EquipmentsPage(): ReactElement {
  const { t } = useTranslation();

  const { employeeId = '' } = useParams<string>();
  const {
    isConfirmDeleteLoading,
    selectedEquipment,
    isDeleteModalOpen,
    handleCloseDeleteModal,
    handleDeleteEquipment,
    onTrashIconClick,
  } = useDeleteEquipment();
  const [state] = useSessionState('i18nextLng');
  const isSwedish = state === Language.swedish;
  const { category, control } = useEquipmentFilter();
  const navigate = useNavigate();

  const createEquipmentLink = generatePath(Links.profile.equipmentList.createEquipment, {
    employeeId,
  });

  return (
    <PageW>
      <GoBackButtonWithTitle
        title={t('common.back')}
        onClick={() => navigate(-1)}
        containerSx={{
          marginTop: 0,
          maxWidth: '100%',
        }}
      />
      <Header>
        <PageTitle>{t('profile.equipments.title')}</PageTitle>

        <HeaderActionsW>
          <CategoryFilter control={control} />
          <CreateButton
            startIcon={
              <AddIcon
                style={{
                  fill: theme.palette.common.white,
                }}
              />
            }
            onClick={() => {
              navigate(createEquipmentLink);
            }}
            style={{
              minWidth: '80px',
              height: '56px',
              padding: '16px',
              fontWeight: 400,
              fontSize: '14px',
              backgroundColor: theme.palette.info.main,
              color: theme.palette.common.white,
              borderRadius: '8px',
              textTransform: 'none',
            }}
          >
            {t('equipments.addEquipments')}
          </CreateButton>
        </HeaderActionsW>
      </Header>
      <ContentW>
        <RegisteredEquipmentsContainer
          isSwedish={isSwedish}
          onTrashIconClick={onTrashIconClick}
          category={category}
        />
        <RequestedEquipmentsContainer
          isSwedish={isSwedish}
          onTrashIconClick={onTrashIconClick}
          category={category}
        />
      </ContentW>

      {!!selectedEquipment && (
        <NewConfirmDialog
          title={t('equipments.removeModal.title', { equipmentName: selectedEquipment.name })}
          subTitle={t('equipments.removeModal.subtitle')}
          open={isDeleteModalOpen}
          onConfirm={handleDeleteEquipment}
          onClose={handleCloseDeleteModal}
          isLoading={isConfirmDeleteLoading}
          submitLabel={t('equipments.removeModal.successButton')}
          cancelLabel={t('equipments.removeModal.cancelButton')}
        />
      )}
    </PageW>
  );
}
